import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Check, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";

const PayPalBox = ({ clientId, onPaid, lang }) => (
  <div data-testid="paywall-paypal-btn" className="bg-white/95 rounded-xl p-3">
    <PayPalScriptProvider options={{ clientId, currency: "EUR", intent: "capture", locale: lang === "fr" ? "fr_FR" : "en_GB" }}>
      <PayPalButtons
        style={{ layout: "vertical", shape: "pill", color: "gold", label: "pay" }}
        createOrder={async () => (await api.post("/payment/paypal/create-order")).data.id}
        onApprove={async (data) => {
          await api.post("/payment/paypal/capture", { order_id: data.orderID, lang });
          await onPaid();
        }}
        onError={() => toast.error("PayPal error")}
      />
    </PayPalScriptProvider>
  </div>
);

export default function Paywall() {
  const { user, refreshUser } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/payment/config").then((r) => setConfig(r.data));
  }, []);

  if (user && user.paid) return <Navigate to="/journal" replace />;

  const onPaid = async () => {
    await refreshUser();
    toast.success(t("pay_success"));
    nav("/record");
  };
  const simulate = async () => {
    setBusy(true);
    try {
      await api.post("/payment/simulate");
      await onPaid();
    } catch (e) {
      toast.error(`${t("pay_error")} ${formatApiError(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid md:grid-cols-[1.1fr_1fr] gap-12 items-center fade-up">
      <div>
        <span data-testid="paywall-status-badge" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#E5C158] border border-[#E5C158]/30 rounded-full px-3 py-1 mb-6">
          <Lock size={12} /> {t("pay_status_locked")}
        </span>
        <p className="label-gold mb-3">{t("pay_kicker")}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F4F6FA] font-semibold leading-tight">{t("pay_title")}</h1>
        <p className="mt-5 text-[#C2C9DB] max-w-md">{t("pay_sub")}</p>
        <ul className="mt-8 space-y-3">
          {["pay_l1", "pay_l2", "pay_l3", "pay_l4"].map((k) => (
            <li key={k} className="flex items-center gap-3 text-[#C2C9DB]">
              <span className="w-6 h-6 rounded-full bg-[#E5C158]/10 border border-[#E5C158]/30 grid place-items-center"><Check size={13} className="text-[#E5C158]" /></span>
              {t(k)}
            </li>
          ))}
        </ul>
      </div>
      <div className="glass-card p-8 sm:p-10 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-[#E5C158]/10 blur-3xl" />
        <p className="label-gold">Écho · Lifetime</p>
        <div className="mt-4 flex items-end gap-3">
          <span className="font-serif text-7xl text-gold-gradient leading-none" data-testid="paywall-price">{config ? `${config.price.replace(".", lang === "fr" ? "," : ".")} €` : "…"}</span>
          <span className="text-sm text-[#8E9BB4] mb-2">{t("pay_once")}</span>
        </div>
        <div className="h-px bg-gradient-to-r from-transparent via-[#E5C158]/40 to-transparent my-8" />
        {!config ? (
          <div className="flex justify-center"><Loader2 className="animate-spin text-[#E5C158]" /></div>
        ) : config.paypal_enabled ? (
          <PayPalBox key={lang} clientId={config.client_id} onPaid={onPaid} lang={lang} />
        ) : (
          <div className="space-y-4">
            <button onClick={simulate} disabled={busy} data-testid="paywall-simulated-pay-btn" className="w-full py-3.5 rounded-full font-semibold text-[#003087] bg-[#FFC439] hover:brightness-105 transition-[filter] flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 size={16} className="animate-spin" />}
              <span className="italic font-bold">PayPal</span> · {t("pay_simulate")}
            </button>
            <p className="text-xs text-[#8E9BB4] text-center" data-testid="paywall-mock-note">{t("pay_mock_note")}</p>
          </div>
        )}
      </div>
    </div>
  );
}
